// =============================================================================
// BP-07-03: useDiagnosis Hook
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import {
  getDiagnoses,
  getDiagnosis,
  getDiagnosisResources,
  getRelatedDiagnoses,
  getDiagnosisSpecialists,
} from '@/features/resources/api/resourcesApi';
import type { PaginationParams } from '@/features/resources/types/resourcesTypes';
import { diagnosisQueryKeys } from '@/features/resources/types/resourcesTypes';

/**
 * Hook for fetching all diagnoses
 */
export function useDiagnoses() {
  return useQuery({
    queryKey: diagnosisQueryKeys.lists(),
    queryFn: getDiagnoses,
    staleTime: 30 * 60 * 1000, // 30 minutes - diagnoses rarely change
  });
}

interface UseDiagnosisOptions {
  slug: string;
  enabled?: boolean;
}

/**
 * Hook for fetching a single diagnosis by slug
 */
export function useDiagnosis(options: UseDiagnosisOptions) {
  const { slug, enabled = true } = options;

  return useQuery({
    queryKey: diagnosisQueryKeys.detail(slug),
    queryFn: () => getDiagnosis(slug),
    enabled: enabled && !!slug,
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * Hook for fetching resources for a specific diagnosis
 */
export function useDiagnosisResources(
  diagnosisSlug: string,
  pagination: PaginationParams = {},
  enabled = true
) {
  return useQuery({
    queryKey: [...diagnosisQueryKeys.resources(diagnosisSlug), pagination],
    queryFn: () => getDiagnosisResources(diagnosisSlug, pagination),
    enabled: enabled && !!diagnosisSlug,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for fetching related diagnoses
 */
export function useRelatedDiagnoses(diagnosisSlug: string, enabled = true) {
  return useQuery({
    queryKey: [...diagnosisQueryKeys.detail(diagnosisSlug), 'related'],
    queryFn: () => getRelatedDiagnoses(diagnosisSlug),
    enabled: enabled && !!diagnosisSlug,
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * Hook for fetching recommended specialists for a diagnosis
 */
export function useDiagnosisSpecialists(diagnosisSlug: string, enabled = true) {
  return useQuery({
    queryKey: [...diagnosisQueryKeys.detail(diagnosisSlug), 'specialists'],
    queryFn: () => getDiagnosisSpecialists(diagnosisSlug),
    enabled: enabled && !!diagnosisSlug,
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * Combined hook for full diagnosis hub data
 */
export function useDiagnosisHub(slug: string) {
  const diagnosis = useDiagnosis({ slug });
  const resources = useDiagnosisResources(slug, { limit: 6 }, !!slug);
  const related = useRelatedDiagnoses(slug, !!slug);
  const specialists = useDiagnosisSpecialists(slug, !!slug);

  return {
    diagnosis,
    resources,
    related,
    specialists,
    isLoading: diagnosis.isLoading,
    isError: diagnosis.isError,
    error: diagnosis.error,
  };
}

export default useDiagnosis;

